import { StepsEnum } from '../types';
import { Step } from './types';

export const SERVICE_STEP_TYPES = [StepsEnum.Seats, StepsEnum.Baggage, StepsEnum.Meal] as const;

const SERVICE_STEP_TYPE_SET = new Set<StepsEnum>(SERVICE_STEP_TYPES);

export function isValidServicesStepsOrder(order: StepsEnum[] | undefined | null): order is StepsEnum[] {
	if (!order || order.length !== SERVICE_STEP_TYPES.length) {
		return false;
	}

	const unique = new Set(order);

	if (unique.size !== SERVICE_STEP_TYPES.length) {
		return false;
	}

	return SERVICE_STEP_TYPES.every(type => unique.has(type));
}

export function applyServicesStepsOrder(steps: Step[], order?: StepsEnum[] | null): Step[] {
	if (!isValidServicesStepsOrder(order)) {
		return steps;
	}

	const serviceIndexes: number[] = [];
	const stepByType = new Map<StepsEnum, Step>();

	steps.forEach((step, index) => {
		if (SERVICE_STEP_TYPE_SET.has(step.type as StepsEnum)) {
			serviceIndexes.push(index);
		}
		stepByType.set(step.type as StepsEnum, step);
	});

	if (serviceIndexes.length !== SERVICE_STEP_TYPES.length) {
		return steps;
	}

	const orderedServices = order.map(type => stepByType.get(type));

	if (orderedServices.some(step => !step)) {
		return steps;
	}

	const result = [...steps];
	serviceIndexes.forEach((index, i) => {
		result[index] = orderedServices[i] as Step;
	});

	return result;
}

export function applyServicesStepsOrderToDefaultSteps<T extends Record<string, unknown>>(
	defaultSteps: T,
	order?: StepsEnum[] | null
): T {
	if (!isValidServicesStepsOrder(order)) {
		return defaultSteps;
	}

	const entries = Object.entries(defaultSteps);
	const serviceKeyByType = new Map<StepsEnum, string>();

	for (const [key] of entries) {
		const matched = SERVICE_STEP_TYPES.find(type => type.toLowerCase() === key.toLowerCase());
		if (matched) {
			serviceKeyByType.set(matched, key);
		}
	}

	const presentOrderedTypes = order.filter(type => serviceKeyByType.has(type));
	if (presentOrderedTypes.length === 0) {
		return defaultSteps;
	}

	const serviceKeysInOrder = presentOrderedTypes.map(type => serviceKeyByType.get(type)!);
	const serviceKeySet = new Set(serviceKeysInOrder);
	let serviceCursor = 0;
	const result: Record<string, unknown> = {};

	for (const [key, value] of entries) {
		if (serviceKeySet.has(key)) {
			const nextKey = serviceKeysInOrder[serviceCursor++];
			result[nextKey] = defaultSteps[nextKey as keyof T];
		} else {
			result[key] = value;
		}
	}

	return result as T;
}
